/**
 * RaceCountdownTimer — sess 16 hero countdown для RaceWeekPage.
 *
 * Показывает days/hours до race; если <24h — minutes. Tick раз в минуту
 * (НЕ раз в секунду — performance + не отвлекает атлета).
 *
 * Stage badge — T-7/T-3/T-1/race_day из current_stage_key.
 *
 * Props:
 *   raceDate: ISO date string (YYYY-MM-DD) - дата старта
 *   raceName: имя гонки
 *   currentStageKey: 'T_30' | 'T_14' | 'T_7' | 'T_3' | 'T_1' | 'race_day' | null
 *   raceStartTime?: HH:MM string (start_time из RaceCatalog) — для точного hh
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReducedMotion } from "../preferences";

interface Props {
  raceDate: string;
  raceName?: string | null;
  currentStageKey?: string | null;
  raceStartTime?: string | null;
}

interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  totalSeconds: number;
  isPast: boolean;
}

const STAGE_LABEL: Record<string, string> = {
  T_30: "T-30",
  T_14: "T-14",
  T_7: "T-7",
  T_3: "T-3",
  T_1: "T-1",
  // T-нотация международная и не переводится; словесная метка — через словарь
  race_day: "",
};

function computeCountdown(
  raceDate: string,
  raceStartTime?: string | null,
): Countdown {
  // Combine race date + optional start time, otherwise default to 08:00 local.
  const startStr = raceStartTime || "08:00";
  const [hh, mm] = startStr.split(":").map((s) => parseInt(s, 10) || 0);
  const race = new Date(raceDate);
  race.setHours(hh, mm, 0, 0);
  const now = new Date();
  const diffMs = race.getTime() - now.getTime();
  const isPast = diffMs <= 0;
  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return { days, hours, minutes, totalSeconds, isPast };
}

export function RaceCountdownTimer({
  raceDate,
  raceName,
  currentStageKey,
  raceStartTime,
}: Props) {
  const { t } = useTranslation("race_week");
  const reducedMotion = useReducedMotion();
  const [countdown, setCountdown] = useState<Countdown>(() =>
    computeCountdown(raceDate, raceStartTime),
  );

  useEffect(() => {
    // Update once per minute - не каждую секунду чтобы не отвлекать.
    // When reduced-motion is on we slow down to 5min — visual change is less
    // distracting (a11y win, performance cost trivial).
    const tickMs = reducedMotion ? 5 * 60_000 : 60_000;
    const interval = setInterval(() => {
      setCountdown(computeCountdown(raceDate, raceStartTime));
    }, tickMs);
    return () => clearInterval(interval);
  }, [raceDate, raceStartTime, reducedMotion]);

  const { days, hours, minutes, isPast, totalSeconds } = countdown;
  const underDay = totalSeconds < 86400 && !isPast;

  return (
    <div
      style={{
        background: "var(--bg-elevated)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
      }}
    >
      {raceName ? (
        <div
          style={{
            fontSize: 14,
            opacity: 0.7,
            marginBottom: 6,
          }}
        >
          {raceName}
        </div>
      ) : null}
      <div
        style={{ display: "flex", alignItems: "baseline", gap: 12 }}
        role="timer"
        aria-live="polite"
        aria-atomic="true"
      >
        {isPast ? (
          <span style={{ fontSize: 24, fontWeight: 600 }}>
            {t("countdown.past")}
          </span>
        ) : underDay ? (
          <>
            <span style={{ fontSize: 40, fontWeight: 700 }}>{hours}</span>
            <span style={{ fontSize: 16 }}>{t("countdown.hours_short")}</span>
            <span style={{ fontSize: 40, fontWeight: 700 }}>{minutes}</span>
            <span style={{ fontSize: 16 }}>{t("countdown.minutes_short")}</span>
          </>
        ) : (
          <>
            <span style={{ fontSize: 40, fontWeight: 700 }}>{days}</span>
            <span style={{ fontSize: 16 }}>{t("countdown.days_short")}</span>
            <span style={{ fontSize: 28, fontWeight: 600, opacity: 0.7 }}>
              {hours}
            </span>
            <span style={{ fontSize: 14, opacity: 0.7 }}>{t("countdown.hours_short")}</span>
          </>
        )}
        {currentStageKey && currentStageKey in STAGE_LABEL ? (
          <span
            style={{
              marginLeft: "auto",
              padding: "4px 10px",
              borderRadius: 999,
              background: "var(--accent-subtle)",
              color: "var(--accent-text)",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {currentStageKey === "race_day"
              ? t("race_stage.race_day", { ns: "plan", defaultValue: "День гонки" })
              : STAGE_LABEL[currentStageKey]}
          </span>
        ) : null}
      </div>
    </div>
  );
}
